import { ref, watch } from 'vue';
import { api } from '@/api/client';
import { useIssuesStore } from '@/stores/issues';
/**
 * Composable that wraps GET /v1/issues/{id} → IssueDetailDto.
 * Optimistically updates on patch/resolve, reverts on API error.
 */
export function useIssue(idRef) {
    const store = useIssuesStore();
    const isLoading = ref(false);
    const error = ref(null);
    // The issue ref mirrors the store entry (full detail shape).
    const issue = ref(null);
    async function fetchDetail() {
        const id = idRef.value;
        if (!id)
            return;
        isLoading.value = true;
        error.value = null;
        try {
            const { data, error: apiError } = await api.GET('/v1/issues/{id}', {
                params: { path: { id } },
            });
            if (apiError || !data) {
                error.value = 'Failed to load issue.';
                return;
            }
            store.upsert(data);
            issue.value = data;
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load issue.';
        }
        finally {
            isLoading.value = false;
        }
    }
    function refresh() {
        void fetchDetail();
    }
    async function patch(req) {
        const id = idRef.value;
        if (!id || !issue.value)
            return;
        const previous = issue.value;
        // Optimistic update
        issue.value = { ...previous, ...req };
        store.upsert(issue.value);
        try {
            const { data, error: apiError } = await api.PATCH('/v1/issues/{id}', {
                params: { path: { id } },
                body: req,
            });
            if (apiError || !data) {
                issue.value = previous;
                store.upsert(previous);
                throw new Error('Failed to update issue.');
            }
            issue.value = data;
            store.upsert(data);
        }
        catch {
            issue.value = previous;
            store.upsert(previous);
            throw new Error('Failed to update issue.');
        }
    }
    async function addComment(req) {
        const id = idRef.value;
        if (!id)
            throw new Error('No issue ID.');
        const { data, error: apiError } = await api.POST('/v1/issues/{id}/comments', {
            params: { path: { id } },
            body: req,
        });
        if (apiError || !data)
            throw new Error('Failed to add comment.');
        // Refresh the full detail to get updated comments list.
        await fetchDetail();
        return data;
    }
    async function resolve() {
        const id = idRef.value;
        if (!id || !issue.value)
            return;
        const previous = issue.value;
        // Optimistic: set status to 3 (Resolved)
        issue.value = { ...previous, status: 3 };
        store.upsert(issue.value);
        try {
            const { data, error: apiError } = await api.POST('/v1/issues/{id}/resolve', {
                params: { path: { id } },
            });
            if (apiError || !data) {
                issue.value = previous;
                store.upsert(previous);
                throw new Error('Failed to resolve issue.');
            }
            issue.value = data;
            store.upsert(data);
        }
        catch {
            issue.value = previous;
            store.upsert(previous);
            throw new Error('Failed to resolve issue.');
        }
    }
    // Fetch when ID changes.
    watch(idRef, () => { void fetchDetail(); }, { immediate: true });
    return {
        issue,
        isLoading,
        error,
        refresh,
        patch,
        addComment,
        resolve,
    };
}
